/**
 * TechnicianTasks Page
 *
 * Mobile-first Kanban view for technician task management.
 * Features swipe navigation, pull-to-refresh, and quick status changes.
 *
 * @module pages/technician/TechnicianTasks
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Container, Alert, Button, InputGroup, Form, Collapse, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { listTasks, updateTaskStatus } from '../../services/taskService';
import { getAssignedProjects } from '../../services/technicianProjectService';
import { unwrapServiceResult } from '../../utils/serviceResult';
import { MobileKanban } from './tasks';
import TaskKanbanView from '../tasks/TaskKanbanView';
import './TechnicianPages.css';

const useIsDesktop = (breakpoint = 768) => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= breakpoint : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${breakpoint}px)`);
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);

  return isDesktop;
};

const TechnicianTasks = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isDesktop = useIsDesktop();
  const [filters, setFilters] = useState({ myTasks: true });
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Build query filters
  const queryFilters = useMemo(() => {
    const apiFilters = {
      assigneeId: filters.myTasks ? user?.id : undefined,
    };

    if (filters.project) {
      apiFilters.projectId = filters.project;
    }
    if (filters.priority) {
      apiFilters.priority = filters.priority;
    }

    return apiFilters;
  }, [filters, user?.id]);

  // Fetch tasks
  const {
    data: tasksResponse,
    isLoading: tasksLoading,
    isError: tasksIsError,
    error: tasksError,
    refetch: refetchTasks,
    isRefetching,
  } = useQuery({
    queryKey: ['technician-tasks', queryFilters],
    // `listTasks` cierra con `catch { return { data:null, count:0, error } }`:
    // RESUELVE. Sin el unwrap esta pantalla ni siquiera podía enterarse de un
    // fallo — `tasks` quedaba en [] y el kanban móvil mostraba tres columnas
    // vacías ("No hay tareas pendientes") con la red caída.
    // Ver `src/utils/serviceResult.js`.
    queryFn: () => unwrapServiceResult(listTasks(queryFilters)),
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
  });

  // Fetch projects for filter
  const { data: projectsResponse } = useQuery({
    queryKey: ['technician-projects', user?.id],
    queryFn: () => getAssignedProjects(user?.id, { pageSize: 50 }),
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Update task status mutation
  //
  // El unwrap acá NO es cosmético: esta mutación es OPTIMISTA. `onMutate`
  // mueve la tarjeta de columna y guarda el snapshot, y el único que revierte
  // es `onError`. Como `updateTaskStatus` -> `updateTask` RESUELVE con
  // `{data:null, error}`, `onError` nunca corría: el rollback y el aviso eran
  // código muerto, y el técnico veía la tarea "completada" sobre un UPDATE que
  // jamás ocurrió. Ver `src/utils/serviceResult.js`.
  const statusMutation = useMutation({
    mutationFn: ({ taskId, status }) => unwrapServiceResult(updateTaskStatus(taskId, status)),
    onMutate: async ({ taskId, status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['technician-tasks'] });

      // Snapshot the previous value
      const previousTasks = queryClient.getQueryData(['technician-tasks', queryFilters]);

      // Optimistically update to the new value
      queryClient.setQueryData(['technician-tasks', queryFilters], (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map(task =>
            task.id === taskId ? { ...task, status } : task
          ),
        };
      });

      return { previousTasks };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      queryClient.setQueryData(['technician-tasks', queryFilters], context.previousTasks);
      setError('Error al actualizar el estado de la tarea');
      setTimeout(() => setError(null), 3000);
    },
    onSettled: () => {
      // Refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['technician-tasks'] });
    },
  });

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    await refetchTasks();
  }, [refetchTasks]);

  // Handle status change from swipe
  const handleStatusChange = useCallback(async (taskId, newStatus) => {
    statusMutation.mutate({ taskId, status: newStatus });
  }, [statusMutation]);

  // Handle create task (navigate to create form)
  const handleCreateTask = useCallback(() => {
    navigate('/tecnico/tareas/nueva');
  }, [navigate]);

  // Handle filter change
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Process tasks data
  const tasks = useMemo(() => {
    let taskList = tasksResponse?.data || [];

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      taskList = taskList.filter(t =>
        t.title?.toLowerCase().includes(search) ||
        t.description?.toLowerCase().includes(search)
      );
    }

    // Enrich tasks with additional metadata
    return taskList.map(task => ({
      ...task,
      has_attachments: task.attachments_count > 0,
      has_comments: task.comments_count > 0,
      has_checklist: task.checklist_items_count > 0,
    }));
  }, [tasksResponse?.data, searchTerm]);

  // Process projects for filter
  const allProjects = useMemo(() => {
    return projectsResponse?.data || [];
  }, [projectsResponse?.data]);

  // Extract unique clients from projects
  const uniqueClients = useMemo(() => {
    const clientMap = new Map();
    allProjects.forEach((p) => {
      if (p.client?.id) {
        clientMap.set(p.client.id, p.client.company_name || p.client.primary_contact_name || 'Sin nombre');
      }
    });
    return Array.from(clientMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allProjects]);

  // Filter projects by selected client (cascade)
  const projects = useMemo(() => {
    if (!filters.client) return allProjects;
    return allProjects.filter((p) => p.client?.id === filters.client);
  }, [allProjects, filters.client]);

  // Build filters for the desktop kanban (TaskKanbanView fetches its own data)
  const desktopKanbanFilters = useMemo(() => {
    const f = {};
    if (filters.myTasks && user?.id) f.assignee = user.id;
    if (filters.client) f.client = filters.client;
    if (filters.project) f.project = filters.project;
    if (filters.priority) f.priority = filters.priority;
    if (searchTerm.trim()) f.search = searchTerm.trim();
    return f;
  }, [filters, user?.id, searchTerm]);

  return (
    <Container fluid className="technician-page p-0">
      {/* Page Header */}
      <div className="page-header px-3 pt-3 pb-2">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1 className="page-title">Mis Tareas</h1>
            <p className="page-subtitle">
              {!isDesktop && tasksIsError
                ? 'No se pudo leer tu lista de tareas'
                : `${tasks.length} tarea${tasks.length !== 1 ? 's' : ''} encontrada${tasks.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          {isDesktop && (
            <button className="btn btn-primary btn-sm" onClick={handleCreateTask}>
              <i className="bi bi-plus-lg me-1"></i>Nueva Tarea
            </button>
          )}
        </div>
      </div>

      {/* Search Bar + Advanced Filters */}
      <div className="px-3 mb-2">
        <InputGroup>
          <InputGroup.Text style={{ background: '#f8f9fa', border: '1px solid #dee2e6', color: '#6c757d' }}>
            <i className="bi bi-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Buscar tareas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Buscar tareas"
          />
          <button
            className={`btn ${filters?.client || filters?.project || filters?.priority ? 'btn-primary' : 'btn-outline-secondary'} d-flex align-items-center gap-1`}
            onClick={() => setShowAdvanced(!showAdvanced)}
            aria-expanded={showAdvanced}
            type="button"
          >
            <i className={`bi bi-chevron-${showAdvanced ? 'up' : 'down'}`}></i>
            <span className="d-none d-sm-inline">Filtros</span>
            {(filters?.client || filters?.project || filters?.priority) && (
              <Badge bg="light" text="dark" pill style={{ fontSize: '0.65rem' }}>
                {(filters?.client ? 1 : 0) + (filters?.project ? 1 : 0) + (filters?.priority ? 1 : 0)}
              </Badge>
            )}
          </button>
        </InputGroup>

        <Collapse in={showAdvanced}>
          <div>
            <div className="d-flex gap-2 mt-2 flex-wrap">
              <Form.Select
                size="sm"
                value={filters?.client || ''}
                onChange={(e) => {
                  const clientId = e.target.value || null;
                  // When client changes, clear project filter if it no longer belongs to the selected client
                  const nextFilters = { ...filters, client: clientId };
                  if (clientId && filters?.project) {
                    const projectStillValid = allProjects.some(
                      (p) => p.id === filters.project && p.client?.id === clientId
                    );
                    if (!projectStillValid) nextFilters.project = null;
                  }
                  handleFilterChange(nextFilters);
                }}
                style={{ maxWidth: '200px' }}
              >
                <option value="">Todos los clientes</option>
                {uniqueClients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
              <Form.Select
                size="sm"
                value={filters?.project || ''}
                onChange={(e) => handleFilterChange({ ...filters, project: e.target.value || null })}
                style={{ maxWidth: '200px' }}
              >
                <option value="">Todos los proyectos</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.title || p.project_number}</option>
                ))}
              </Form.Select>
              <Form.Select
                size="sm"
                value={filters?.priority || ''}
                onChange={(e) => handleFilterChange({ ...filters, priority: e.target.value || null })}
                style={{ maxWidth: '150px' }}
              >
                <option value="">Todas las prioridades</option>
                <option value="urgent">Urgente</option>
                <option value="high">Alta</option>
                <option value="medium">Media</option>
                <option value="low">Baja</option>
              </Form.Select>
              {(filters?.client || filters?.project || filters?.priority) && (
                <button className="btn btn-sm btn-outline-secondary" onClick={() => handleFilterChange({ myTasks: filters.myTasks }) } type="button">
                  <i className="bi bi-x-lg me-1"></i>Limpiar
                </button>
              )}
            </div>
          </div>
        </Collapse>
      </div>

      {/* Error de MUTACIÓN (cambio de estado). Es descartable a propósito: la
          lista sigue siendo legible detrás. */}
      {error && (
        <Alert variant="danger" className="mx-3 mb-2" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Error de LECTURA. Sólo en el camino móvil: en escritorio los datos de
          esta query no se usan (TaskKanbanView hace la suya y muestra su propio
          error), así que repetirlo acá sería ruido. NO es descartable —
          descartarlo dejaría el kanban vacío mintiendo. */}
      {!isDesktop && tasksIsError && (
        <Alert variant="danger" className="mx-3 mb-2">
          <Alert.Heading className="h6">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Error al cargar tareas
          </Alert.Heading>
          <p className="mb-2 small">{tasksError?.message || 'Intente nuevamente'}</p>
          <Button variant="outline-danger" size="sm" onClick={() => refetchTasks()}>
            <i className="bi bi-arrow-clockwise me-1" aria-hidden="true"></i>
            Reintentar
          </Button>
        </Alert>
      )}

      {/* Desktop: Admin-style Kanban with drag & drop */}
      {/* Mobile: Swipe-based MobileKanban */}
      {isDesktop ? (
        <div className="px-3 pb-3">
          <TaskKanbanView
            filters={desktopKanbanFilters}
            basePath="/tecnico/tareas"
          />
        </div>
      ) : tasksIsError ? null : (
        <MobileKanban
          tasks={tasks}
          loading={tasksLoading || isRefetching}
          onRefresh={handleRefresh}
          onStatusChange={handleStatusChange}
          onCreateTask={handleCreateTask}
          filters={filters}
          onFilterChange={handleFilterChange}
          projects={projects}
        />
      )}
    </Container>
  );
};

export default TechnicianTasks;
